/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosResponse } from "axios";
import { z } from "zod";

import {
  ApiErrorResponse,
  ApiLogInfo,
  ApiResponse,
  ApiStatus,
} from "../types/api.types";
import {
  CustomApiException,
  InvalidDataApiException,
  NetworkApiException,
  isAxiosNetworkError,
} from "@/exceptions/custom-api-error";
import { ApiHeaders } from "@/common/hooks/headers.types";
import { REGEXP } from "@/common/constants/regex.constants";
import { axiosInstance, fileUploadAxiosInstance } from "./axios-instance";
import { logger } from "@/common/utils/logger";

export enum ApiRequestMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

type ApiHandlerParams<T> = {
  url: string;
  method: ApiRequestMethod;
  headers: ApiHeaders;
  params?: Record<string, string>;
  requestBody?: T;
  encryption?: boolean;
};

export const extractContextSegment = (url: string): string | undefined => {
  const match = url.match(REGEXP.apiContextSegment); // Match "/api/segment"
  return match ? match[1] : "";
};

export async function apiHandler<T, R>(
  config: ApiHandlerParams<T>,
  schema?: z.Schema,
): Promise<AxiosResponse<R>> {
  const timeStamp = new Date().toISOString();
  const context = extractContextSegment(config.url);

  const apiLoggerDetails: ApiLogInfo = {
    timeStamp,
    url: config.url,
    requestId: config.headers["request-id"],
    httpCode: undefined,
    errorCode: undefined,
    message: undefined,
    responseTime: Date.now(),
  };
  try {
    const instance = config.requestBody instanceof FormData ? fileUploadAxiosInstance : axiosInstance;
    const response = await instance({
      method: config.method,
      url: config.url,
      headers: config.headers,
      params: config.params,
      data: config.requestBody,
    });
    const data = response.data as ApiResponse<R>;
    if (
      response.data.status_code === 200 ||
      data.statusCode === 200 ||
      data.statusCode === "S" ||
      data.success
    ) {
      logAPISuccessResponse(apiLoggerDetails);

      if (schema) {
        schema.parse(data);
      }

      return response;
    }

    if (response.data.status_code !== 200 || response.data.statusCode !== "S") {
      throw new CustomApiException(
        data as ApiErrorResponse,
        apiLoggerDetails,
        context,
      );
    }

    throw new InvalidDataApiException(
      config.headers["request-id"],
      timeStamp,
      data?.statusCode,
      ApiStatus.FAILURE,

      apiLoggerDetails,
    );
  } catch (err: any) {
    if (isAxiosNetworkError(err)) {
      throw new NetworkApiException(
        config.headers["request-id"],
        timeStamp,
        apiLoggerDetails,
      );
    }

    if (axios.isAxiosError(err) && err.response?.data?.error) {
      throw new CustomApiException(
        err.response.data,
        apiLoggerDetails,
        context,
      );
    }

    throw err;
  }
}

function logAPISuccessResponse(apiLoggerDetails: ApiLogInfo) {
  logger.apiInfo({
    ...apiLoggerDetails,
    responseTime: Date.now() - apiLoggerDetails.responseTime,
  });
}
